import { normalizeShortcut } from '../../shortcuts/keys'
import { normalizeViewPrefs } from '../../utils/viewPrefs'
import { normalizeCharacterKeys } from '../../utils/characterKeys'
import { normalizeThemeSettings } from '../../services/theme/themeEngine'
import { normalizeDocumentTemplates } from '../../services/corpus/documentTemplates'
import { normalizePageStatuses } from '../../services/pages/pageStatus'
import { normalizePageViewDisplay } from '../../utils/pageViewDisplay'
import { normalizeListPrefs } from '../../utils/listStyles'
import { normalizeBarLayout } from '../../utils/barLayout'
import { normalizeGoalDays } from '../../utils/goalModel'
import { normalizeCalendarPrefs, normalizeDocketPrefs, normalizeGoalPrefs, normalizeToolPrefs } from '../../utils/toolSettings'
import { normalizeWritingHabit } from './writingHabit'
import { normalizeLooseEndSettings } from './looseEnds'
import { normalizeEditorModeSettings } from './editorMode'
import { normalizeProofingSettings } from './proofing'
import { deepMergeTypography } from './typography'
import { deepMergeIconStyles } from './iconStyles'
import { deepMergeContextMenuItems } from './contextMenu'

/**
 * The writer's own keys for shortcuts (Settings › Shortcuts), by action id;
 * an empty list means the action has no key. Actions not listed keep their
 * defaults (shortcuts/actions.ts).
 */
export type ShortcutOverrides = Record<string, string[]>

export function normalizeShortcutOverrides(raw: any): ShortcutOverrides {
  const out: ShortcutOverrides = {}
  if (!raw || typeof raw !== 'object') return out
  for (const [id, keys] of Object.entries(raw)) {
    if (!/^[a-zA-Z][\w.]*$/.test(id) || !Array.isArray(keys)) continue
    const valid = keys.filter((key): key is string => typeof key === 'string').map(normalizeShortcut).filter((key): key is string => !!key)
    out[id] = [...new Set(valid)].slice(0, 4)
  }
  return out
}

/** Settings checked by their own normalizer when they arrive from elsewhere. */
const KEY_NORMALIZERS: Record<string, (raw: any) => any> = {
  fontFamily: (raw) => raw || "'Cutive', Georgia, serif",
  theme: normalizeThemeSettings,
  writingHabit: normalizeWritingHabit,
  goalDays: normalizeGoalDays,
  looseEnds: normalizeLooseEndSettings,
  editorMode: normalizeEditorModeSettings,
  pageStatuses: normalizePageStatuses,
  proofing: normalizeProofingSettings,
  documentTemplates: normalizeDocumentTemplates,
  typography: deepMergeTypography,
  iconStyles: deepMergeIconStyles,
  contextMenuItems: deepMergeContextMenuItems,
  toolPrefs: normalizeToolPrefs,
  calendarPrefs: normalizeCalendarPrefs,
  goalPrefs: normalizeGoalPrefs,
  docketPrefs: normalizeDocketPrefs,
  pageViewDisplay: normalizePageViewDisplay,
  listPrefs: normalizeListPrefs,
  viewPrefs: normalizeViewPrefs,
  shortcutOverrides: normalizeShortcutOverrides,
  characterKeys: normalizeCharacterKeys,
  desktopBarLayout: (raw) => normalizeBarLayout(raw, 'desktop'),
  phoneBarLayout: (raw) => normalizeBarLayout(raw, 'phone'),
}

/** A setting's value as it arrives from the cloud or an import, made safe to use. */
export function normalizeSettingValue(key: string, value: any): any {
  const normalize = KEY_NORMALIZERS[key]
  return normalize ? normalize(value) : JSON.parse(JSON.stringify(value))
}
